import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment";
import { Card, Container, Segment } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import * as corsiApi from "../../api/corsiApi";
import * as privateNewApi from "../../api/privateNewApi";
import * as appActions from "../../redux/actions/appActions";
import * as cartActions from "../../redux/actions/cartActions";
import SchedaCorsoCard from "../collettive/SchedaCorsoCard";
import SchedaOffertaCard from "./SchedaOffertaCard";
import RedirectWithTrack from "../common/RedirectWithTrack";
import DettaglioOfferta from "./DettaglioOfferta";
import DettaglioOffertaModal from "./DettaglioOffertaModal";
import TestataPagina from "../common/TestataPagina";
import {
  groupPrivateOffers,
  selectVisibleOffers,
  courseRunsNearSelectedDays,
} from "../../utils/privateOffersUtils";

/**
 * Sezione "Offerte / Pacchetti" in fondo all'area lezioni private.
 *
 * Montata da tre host, che ne selezionano il comportamento tramite le prop passate:
 * - PrivateMain: nessuna prop di queste. Layout legacy invariato (niente gating, dettaglio inline,
 *   header "Offerte / Pacchetti").
 * - PrivateNewMain: passa `onOfferSelected` (oltre a `isPreferenzeValid`, `selectedDates`,
 *   `numeroAllieviSelezionati`, `appliedOfferKey`, `onAppliedOfferInvalidated`). Layout "shelf"
 *   (gating su preferenze/date, dettaglio in modale, header allineato al nuovo portale) PIÙ le
 *   card ListinoOfferta.
 * - FastMain: passa `shelf` (senza `onOfferSelected`, ma con `isPreferenzeValid`, `selectedDates`,
 *   `numeroAllieviSelezionati`, `senzaFiltroGiorni`). Stesso layout "shelf" di PrivateNewMain, ma
 *   SENZA le card ListinoOfferta (non sa come applicarle).
 *
 * Renderizza il proprio Container in tutti i casi, così le varianti restano identiche a livello di
 * layout esterno. Se la scuola non ha offerte non renderizza nulla.
 *
 * Le sessioni vengono caricate in stato locale e NON tramite corsiActions.loadSessioni: quello
 * scrive su `state.corsi.sessioni`, uno slot globale condiviso col flusso di dettaglio dei corsi
 * collettivi, che verrebbe sovrascritto.
 *
 * Le offerte su lezione privata (ListiniOfferta, tabella diversa dai CorsoPrivato sopra) si
 * caricano e mostrano SOLO quando `onOfferSelected` è passato: PrivateMain e FastMain non lo
 * passano e non cambiano comportamento, PrivateNewMain sì. Cliccare una card richiama
 * `onOfferSelected` con l'offerta scelta, così chi monta questo componente decide come applicarla
 * (per PrivateNewMain, preselezionando giorno/orario/persone nel suo calendario già esistente).
 *
 * Il fetch parte appena montate (dato piccolo, non dipende dai filtri), ma la VISIBILITÀ delle
 * card offerta è condizionata da `isPreferenzeValid` (niente prezzo/orario senza sapere per cosa)
 * e poi da `selectVisibleOffers`, la stessa regola del nuovo portale: numero di persone coincidente
 * e OGNI giorno selezionato dentro la validità dell'offerta. Le card dei pacchetti CorsoPrivato
 * sopra seguono solo il gate sulle preferenze, non le date.
 */
function OffersSection({
  appData,
  datiScuola,
  promozioniNew,
  cart,
  setCorsoDettagli,
  setPrefsCorso,
  setReferrer,
  onOfferSelected,
  isPreferenzeValid,
  numeroAllieviSelezionati,
  selectedDates,
  appliedOfferKey,
  onAppliedOfferInvalidated,
  shelf,
  senzaFiltroGiorni,
}) {
  const [offerte, setOfferte] = useState([]);
  const [idCorsoSelezionato, setIdCorsoSelezionato] = useState(null);
  const [corsoSelezionato, setCorsoSelezionato] = useState(null);
  const [sessioniCorso, setSessioniCorso] = useState(null);
  const [redirectToAllievi, setRedirectToAllievi] = useState(false);
  const [offerteListino, setOfferteListino] = useState([]);

  const { t, i18n } = useTranslation([datiScuola?.customTranslationNamespace]);

  useEffect(() => {
    i18n.emit("languageChanged");
  }, [datiScuola?.customTranslationNamespace]);

  useEffect(() => {
    if (!datiScuola?.id) return;

    let cancelled = false;
    (async () => {
      const res = await corsiApi.getListaOfferte(
        datiScuola.id,
        moment().format("YYYY-MM-DD"),
        datiScuola.idLinguaInUso,
        appData?.agenzia?.idAgenzia ?? 0
      );
      if (!cancelled) setOfferte(Array.isArray(res) ? res : []);
    })();

    return () => {
      cancelled = true;
    };
  }, [datiScuola?.id, datiScuola?.idLinguaInUso, appData?.agenzia?.idAgenzia]);

  // Solo per PrivateNewMain: PrivateMain non passa onOfferSelected, quindi non fa questa
  // chiamata e resta esattamente come prima.
  useEffect(() => {
    if (!onOfferSelected) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await privateNewApi.getOffertePrivate(
          appData?.agenzia?.idAgenzia ?? 0
        );
        if (!cancelled) setOfferteListino(groupPrivateOffers(res));
      } catch (err) {
        if (!cancelled) setOfferteListino([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [onOfferSelected, appData?.agenzia?.idAgenzia]);

  // In PrivateNewMain e FastMain (layout "shelf": onOfferSelected oppure shelf presente) l'intera
  // sezione, header compreso, resta nascosta finché le preferenze non sono valide: mostrare
  // pacchetti/offerte prima non ha senso, non si sa ancora per cosa. In PrivateMain (nessuna delle
  // due prop) non esiste questo concetto ed è esattamente il comportamento di sempre: i pacchetti
  // CorsoPrivato si vedono appena caricati.
  // Calcolato qui (prima ancora degli hook sotto) perché sia handleCorsoSelezionato sia gli hook
  // di visibilità/invalidazione ne hanno bisogno.
  // isShelf: layout "shelf" (gating su preferenze/date, dettaglio in modale, header nuovo stile),
  // condiviso da PrivateNewMain e FastMain.
  const isShelf = Boolean(shelf) || Boolean(onOfferSelected);
  // conOfferteListino: le offerte ListinoOfferta esistono solo dove c'è chi sa applicarle
  // (PrivateNewMain, tramite onOfferSelected). FastMain ha isShelf=true ma non le mostra.
  const conOfferteListino = Boolean(onOfferSelected);
  const preferenzeReady = !isShelf || isPreferenzeValid;

  // Nello shelf i pacchetti CorsoPrivato seguono i giorni scelti, come nel nuovo portale: si
  // mostrano solo se il corso gira vicino ad almeno uno di quelli scelti (regola dei ±7 giorni).
  // Eccezione per FastMain (`senzaFiltroGiorni`): lì si mostrano tutti appena superato il gate
  // sulle preferenze (specialità scelta), giorno scelto o meno, perché in questa pagina il giorno
  // riguarda solo la scelta della fascia oraria fissa, non i pacchetti.
  const corsiVisibili = !preferenzeReady
    ? []
    : isShelf && !senzaFiltroGiorni
      ? offerte.filter((corso) =>
          courseRunsNearSelectedDays(corso, selectedDates)
        )
      : offerte;

  // Memoizzato: viene ricalcolato solo quando cambia uno degli input reali, non a ogni render.
  // Necessario perché più sotto viene usato come dipendenza di un useEffect (invalidazione
  // dell'offerta applicata): un nuovo array a ogni render farebbe ripartire l'effetto in loop.
  const offerteListinoVisibili = useMemo(() => {
    return conOfferteListino && isPreferenzeValid
      ? selectVisibleOffers(offerteListino, selectedDates, numeroAllieviSelezionati)
      : [];
  }, [conOfferteListino, isPreferenzeValid, offerteListino, selectedDates, numeroAllieviSelezionati]);

  // Solo per PrivateNewMain (appliedOfferKey arriva solo da lì): aggiungere un giorno fuori dalla
  // validità dell'offerta applicata non cambia il primo giorno selezionato, quindi nessuno dei
  // reset esistenti scatta e la fascia resterebbe nella timeline a pubblicizzare un prezzo che il
  // cliente non può più ottenere. Porting di YouMayAlsoLikeBox.tsx (PortalHybrid).
  useEffect(() => {
    if (!appliedOfferKey) return;
    if (offerteListinoVisibili.some((g) => g.key === appliedOfferKey)) return;
    // Chiamata guardata invece di `?.()`: la toolchain eslint di questo progetto (v6.8.0) non
    // digerisce l'optional call come statement (falso positivo di no-unused-expressions).
    if (onAppliedOfferInvalidated) {
      onAppliedOfferInvalidated();
    }
  }, [appliedOfferKey, offerteListinoVisibili, onAppliedOfferInvalidated]);

  async function handleCorsoSelezionato(idCorso) {
    // Nel flusso "shelf" (PrivateNewMain, FastMain) il dettaglio si apre in modale
    // (DettaglioOffertaModal), che ha una propria chiusura: il toggle "riclicca per chiudere" del
    // flusso legacy qui non si applica.
    if (!isShelf && idCorsoSelezionato === idCorso) {
      setIdCorsoSelezionato(null);
      setCorsoSelezionato(null);
      setSessioniCorso(null);
      return;
    }

    const corso = offerte.find((x) => x.idCorso === idCorso);
    if (!corso) return;

    setIdCorsoSelezionato(idCorso);
    setCorsoSelezionato(corso);
    setSessioniCorso(null);

    const res = await corsiApi.getListaSessioni(
      datiScuola.id,
      corso.id,
      moment().format("YYYY-MM-DD"),
      datiScuola.idLinguaInUso,
      appData?.agenzia?.idAgenzia ?? -1
    );
    setSessioniCorso(res);
  }

  // Chiude la modale del flusso "shelf" (PrivateNewMain, FastMain) e azzera la selezione, così può
  // essere riaperta pulita su un altro pacchetto.
  function handleCloseCorsoModal() {
    setIdCorsoSelezionato(null);
    setCorsoSelezionato(null);
    setSessioniCorso(null);
  }

  function handleAvanti(
    primaData,
    giorni,
    numSessioni,
    allievi,
    prezzo,
    prezzoPag,
    prezzoListino,
    listaDate,
    scontoPromozione,
    scontoAgenzia,
    prezzoListinoRiferimento
  ) {
    if (cart.righeCollettive.find((x) => x.roomingList === true) !== undefined) {
      alert(
        "Nel carrello è già presente un corso con rooming list, non è possibile aggiungere un altro corso"
      );
      return;
    }

    setCorsoDettagli(corsoSelezionato);
    setPrefsCorso({
      primaData: primaData,
      numGiorni: giorni,
      numSessioni: numSessioni,
      numAllievi: allievi,
      prezzo: prezzo,
      prezzoPag: prezzoPag,
      prezzoListino: prezzoListino,
      prezzoListinoRiferimento: prezzoListinoRiferimento,
      listaDate: listaDate,
      scontoPromozione: scontoPromozione || 0,
      scontoAgenzia: scontoAgenzia || 0,
      // Il prezzo di un pacchetto è già il totale della comitiva.
      prezzoTotale: true,
      origine: "private",
    });
    setReferrer("private");
    setRedirectToAllievi(true);
  }

  // La modale (flusso "shelf": PrivateNewMain, FastMain) non si autochiude alla conferma: senza
  // questo wrapper resterebbe montata sopra il redirect verso la pagina allievi. handleAvanti va
  // invocato per primo perché legge corsoSelezionato dallo stato corrente, azzerato subito dopo.
  function handleAvantiFromModal(...args) {
    handleAvanti(...args);
    handleCloseCorsoModal();
  }

  if (corsiVisibili.length === 0 && offerteListinoVisibili.length === 0) {
    return null;
  }

  // Card + eventuale dettaglio: estratti così da poterli avvolgere in un Segment "vero" solo per
  // il flusso "shelf" (PrivateNewMain, FastMain; vedi sotto), senza duplicare il JSX tra i flussi.
  const contenutoShelf = (
    <>
      <Card.Group centered style={isShelf ? undefined : { marginTop: "20px" }}>
        {corsiVisibili.map((corso) => (
          <SchedaCorsoCard
            key={corso.idCorso}
            t={t}
            codScuola={datiScuola.codicePrimario || datiScuola.codiceScuola}
            corso={corso}
            isVisible={true}
            onSelect={handleCorsoSelezionato}
            // Nel flusso "shelf" (PrivateNewMain, FastMain) le card non hanno immagine, come nel
            // nuovo portale.
            hideImage={isShelf}
          />
        ))}
        {offerteListinoVisibili.map((offerta) => (
          <SchedaOffertaCard
            key={offerta.key}
            t={t}
            offerta={offerta}
            valuta={datiScuola.valuta}
            isVisible={true}
            onSelect={onOfferSelected}
            // Evidenzia la card la cui fascia è già stata applicata alla timeline sopra.
            isApplied={offerta.key === appliedOfferKey}
          />
        ))}
      </Card.Group>

      {/* Flusso legacy (PrivateMain): dettaglio inline sotto la griglia, invariato. */}
      {!isShelf && corsoSelezionato && sessioniCorso && (
        <DettaglioOfferta
          corso={corsoSelezionato}
          sessioniCorso={sessioniCorso}
          datiScuola={datiScuola}
          promozioniNew={promozioniNew}
          appData={appData}
          onAvantiClick={handleAvanti}
        />
      )}

      {/* Flusso "shelf" (PrivateNewMain, FastMain): dettaglio in modale, come OfferCourseDialog
          del nuovo portale. key sull'idCorso: riaprendo un pacchetto diverso la modale viene
          rimontata da zero e non può ereditare periodo/numero persone del pacchetto precedente.
          sessioniCorso resta null finché il fetch è in corso: la modale mostra da sé lo stato di
          caricamento. La posizione nell'albero non conta: la modale renderizza tramite portale. */}
      {isShelf && corsoSelezionato && (
        <DettaglioOffertaModal
          key={corsoSelezionato.idCorso}
          open={true}
          onClose={handleCloseCorsoModal}
          corso={corsoSelezionato}
          sessioniCorso={sessioniCorso}
          datiScuola={datiScuola}
          promozioniNew={promozioniNew}
          appData={appData}
          selectedDates={selectedDates}
          preferredNumAllievi={numeroAllieviSelezionati}
          onAvantiClick={handleAvantiFromModal}
        />
      )}
    </>
  );

  return (
    <Container>
      {redirectToAllievi && (
        <RedirectWithTrack
          to={`/${appData.lang}/${appData.slug}/collettive/allievi`}
          push
        />
      )}
      <Segment basic style={{ marginBottom: "30px", padding: "0" }}>
        {/* Header allineato al nuovo portale (freccetta + "ti potrebbe interessare") per il flusso
            "shelf" (PrivateNewMain, FastMain); PrivateMain mantiene titolo/icona di sempre. */}
        <TestataPagina
          titolo={isShelf ? t("ti_potrebbe_interessare") : t("offerte_pacchetti")}
          icon={isShelf ? "chevron right" : "gift"}
        />

        {/* Flusso "shelf" (PrivateNewMain, FastMain): Segment non-basic per allinearsi al riquadro
            "Lezioni private" sopra (TestataPagina + Segment in PrivateNewMainDesktop/FastMain), che
            dà il pannello bianco e il padding al posto del marginTop ad hoc. PrivateMain mantiene
            il layout storico, senza pannello, con le card a diretto contatto con lo sfondo pagina. */}
        {isShelf ? <Segment>{contenutoShelf}</Segment> : contenutoShelf}
      </Segment>
    </Container>
  );
}

OffersSection.propTypes = {
  appData: PropTypes.object.isRequired,
  datiScuola: PropTypes.object.isRequired,
  cart: PropTypes.object.isRequired,
  setCorsoDettagli: PropTypes.func.isRequired,
  setPrefsCorso: PropTypes.func.isRequired,
  setReferrer: PropTypes.func.isRequired,
  onOfferSelected: PropTypes.func,
  isPreferenzeValid: PropTypes.bool,
  numeroAllieviSelezionati: PropTypes.number,
  selectedDates: PropTypes.array,
  appliedOfferKey: PropTypes.string,
  onAppliedOfferInvalidated: PropTypes.func,
  shelf: PropTypes.bool,
  senzaFiltroGiorni: PropTypes.bool,
};

// datiScuola arriva da scuolaReducer, NON da scuolaNewReducer: PrivateNewMain mappa quest'ultimo,
// quindi la sezione non può ereditarlo come prop.
function mapStateToProps(state) {
  return {
    appData: state.appReducer,
    datiScuola: state.scuolaReducer,
    promozioniNew: state.promozioniNewReducer,
    cart: state.cartReducer,
  };
}

const mapDispatchToProps = {
  setCorsoDettagli: appActions.setCorsoDettagli,
  setPrefsCorso: appActions.setPrefsCorso,
  setReferrer: cartActions.setReferrer,
};

export default connect(mapStateToProps, mapDispatchToProps)(OffersSection);
